import {
  currentConsentValue,
  isAperioHost,
  parseConsentCookie,
  readCookie,
  rememberConsentValue,
} from './aperioTracking';

/**
 * The booking portal's own consent choice, for a visitor who lands on
 * book.aperiomobility.com without having made one on aperiomobility.com
 * (no `apr_consent` cookie). Same contract as the site
 * (aperio/site/src/static/js/consent.js, docs/TRACKING.md):
 *
 * - ask-first in Quebec, California, the EU/EEA, the UK and Switzerland:
 *   nothing runs until Accept all;
 * - everywhere else an implied grant with a notice that offers Accept all /
 *   Decline all, written as `i` (implied) like the site's;
 * - Global Privacy Control: no bar, nothing granted (a refusal everywhere).
 *
 * The region comes from the marketing site's `/geo.json` (its CloudFront
 * function reads CloudFront's viewer headers and lets this one origin read
 * the answer). Unlike the site, a region the portal cannot learn - no
 * answer, a timeout, or a country whose ask-first region is missing (CA
 * without the province, US without the state) - counts as ask-first: the
 * portal never grants without knowing it may. One fallback narrows that
 * (2026-10-24): the device's own time zone, read locally, when it can only
 * be outside every ask-first region (America/Vancouver cannot be Quebec,
 * California or Europe). America/Toronto (Quebec shares it),
 * America/Los_Angeles (California), Europe/* and anything unknown stay
 * ask-first.
 *
 * The answer goes into the same cookie the site writes, so both hosts read
 * one decision: `v1.a<0|1>.d<0|1>.<e|i>.<unixSeconds>` on
 * .aperiomobility.com, 12 months.
 */

export const CONSENT_COOKIE = 'apr_consent';
export const GEO_URL = 'https://aperiomobility.com/geo.json';
const GEO_KEY = 'aperio-geo';
const GEO_TIMEOUT_MS = 2500;
const COOKIE_MAX_AGE_S = 365 * 24 * 60 * 60;
const SITE_DOMAIN = 'aperiomobility.com';

/** Opt-in before analytics: EU 27, the non-EU EEA states, the UK, Switzerland. */
export const ASK_FIRST_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO',
  'GB',
  'CH',
];
/** Country -> the region inside it that asks first. */
export const ASK_FIRST_REGIONS: Record<string, string> = { CA: 'QC', US: 'CA' };

export interface Geo {
  country: string;
  region: string;
}

export type ConsentMode = 'ask-first' | 'implied';

/**
 * Canadian time zones no part of Quebec uses (Quebec is America/Toronto,
 * which Ontario shares, plus America/Blanc-Sablon on its Lower North Shore),
 * and US ones California does not (it is America/Los_Angeles only).
 */
const NOT_QUEBEC_ZONES = [
  'America/Vancouver', 'America/Edmonton', 'America/Winnipeg', 'America/Regina',
  'America/Swift_Current', 'America/Whitehorse', 'America/Dawson', 'America/Dawson_Creek',
  'America/Fort_Nelson', 'America/Creston', 'America/Yellowknife', 'America/Inuvik',
  'America/Cambridge_Bay', 'America/Rankin_Inlet', 'America/Resolute', 'America/Halifax',
  'America/Glace_Bay', 'America/Moncton', 'America/Goose_Bay', 'America/St_Johns',
];
const NOT_CALIFORNIA_ZONES = [
  'America/New_York', 'America/Detroit', 'America/Indiana/Indianapolis', 'America/Kentucky/Louisville',
  'America/Chicago', 'America/Denver', 'America/Boise', 'America/Phoenix', 'America/Anchorage',
  'America/Juneau', 'America/Sitka', 'America/Nome', 'America/Adak', 'Pacific/Honolulu',
];

/** The device's IANA time zone, or '' (read locally; never sent or stored). */
export function deviceTimeZone(): string {
  try {
    return String(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
  } catch {
    return '';
  }
}

/**
 * The region the time zone rules out of every ask-first region: 'CA' for a
 * Canadian zone outside Quebec, 'US' for a US zone outside California, null
 * when it cannot rule anything out.
 */
export function zoneOutsideAskFirst(timeZone: string): 'CA' | 'US' | null {
  if (NOT_QUEBEC_ZONES.includes(timeZone)) return 'CA';
  if (NOT_CALIFORNIA_ZONES.includes(timeZone)) return 'US';
  return null;
}

/**
 * Which rule applies where the visitor is. The CDN's answer decides; when
 * it is missing or incomplete (no answer, or CA / US without the province /
 * state) the device's time zone may settle it (zoneOutsideAskFirst), and
 * otherwise it is ask-first.
 */
export function consentMode(
  geo: Geo | null | undefined,
  timeZone = '',
): ConsentMode {
  const country = String(geo?.country || '').toUpperCase();
  const region = String(geo?.region || '').toUpperCase();
  const zoneCountry = zoneOutsideAskFirst(timeZone);
  if (!country) return zoneCountry ? 'implied' : 'ask-first';
  if (ASK_FIRST_COUNTRIES.includes(country)) return 'ask-first';
  const askRegion = ASK_FIRST_REGIONS[country];
  if (askRegion && region === askRegion) return 'ask-first';
  if (askRegion && !region) return zoneCountry === country ? 'implied' : 'ask-first';
  return 'implied';
}

const hasGpc = (): boolean => {
  try {
    return (navigator as unknown as { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true;
  } catch {
    return false;
  }
};

/**
 * The bar is offered on an Aperio host when no decision is on file and the
 * browser sends no Global Privacy Control.
 */
export function needsConsentChoice(host?: string): boolean {
  if (!isAperioHost(host)) return false;
  if (parseConsentCookie(currentConsentValue())) return false;
  return !hasGpc();
}

/** The cookie value, exactly as the site writes it. */
export function consentCookieValue(
  choice: { analytics: boolean; ads: boolean },
  mode: 'explicit' | 'implied',
  now: number = Date.now(),
): string {
  const ads = choice.ads && !hasGpc();
  return `v1.a${choice.analytics ? 1 : 0}.d${ads ? 1 : 0}.${mode === 'implied' ? 'i' : 'e'}.${Math.floor(now / 1000)}`;
}

/**
 * Writes the decision on .aperiomobility.com (host-only anywhere else), and
 * keeps it in memory for this page view in case the cookie is refused.
 */
export function writeConsentCookie(value: string): void {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname.toLowerCase();
  const onSite = host === SITE_DOMAIN || host.endsWith(`.${SITE_DOMAIN}`);
  const parts = [
    `${CONSENT_COOKIE}=${value}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE_S}`,
    'SameSite=Lax',
  ];
  if (onSite) parts.push(`Domain=.${SITE_DOMAIN}`);
  if (window.location.protocol === 'https:') parts.push('Secure');
  try {
    document.cookie = parts.join('; ');
  } catch {
    // cookies blocked: remembered in memory below
  }
  // Cookies refused: the decision still holds for this page view (the
  // booking records it), and the bar shows again next time.
  rememberConsentValue(readCookie(CONSENT_COOKIE) === value ? null : value);
}

const codeOnly = (v: unknown): string => String(v || '').replace(/[^A-Za-z0-9-]/g, '').slice(0, 8);

/**
 * The visitor's country and region from the site's /geo.json, once per
 * session (sessionStorage). Null when it cannot be learnt in time.
 */
export async function fetchGeo(
  fetchImpl: typeof fetch = typeof fetch !== 'undefined' ? fetch : (undefined as never),
  timeoutMs: number = GEO_TIMEOUT_MS,
): Promise<Geo | null> {
  try {
    const cached = sessionStorage.getItem(GEO_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed.country === 'string') {
        return { country: codeOnly(parsed.country), region: codeOnly(parsed.region) };
      }
    }
  } catch {
    // no sessionStorage: ask the network
  }
  if (!fetchImpl) return null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const answer = await Promise.race([
      fetchImpl(GEO_URL, { credentials: 'omit', cache: 'no-store' })
        .then((res) => (res.ok ? res.json() : null)),
      new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), timeoutMs); }),
    ]);
    if (!answer || typeof answer.country !== 'string') return null;
    const geo = { country: codeOnly(answer.country), region: codeOnly(answer.region) };
    try {
      sessionStorage.setItem(GEO_KEY, JSON.stringify(geo));
    } catch {
      // fine: asked again next page
    }
    return geo;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
